import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Briefcase, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm_password: "", role: "candidate", employer_number: "" });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      const payload = { name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role };
      if (form.role === "employer") payload.employer_number = form.employer_number.trim();
      const res = await register(payload);
      if (res?.needs_verification) {
        navigate("/verify-email", { state: { email: res.email || payload.email } });
        return;
      }
      // Fallback (legacy backend that still auto-logs-in)
      toast.success(t("auth.welcome"));
      navigate("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const detailStr = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || "")
          : "";
      let msg;
      if (!err?.response) {
        // No response = network / CORS / connectivity failure
        msg = t("auth.networkError");
      } else if (/email|correo/i.test(detailStr) && status === 400) {
        msg = t("auth.emailTaken");
      } else if (form.role === "employer" && (status === 400 || status === 422) && /emplead|employer/i.test(detailStr)) {
        msg = t("auth.employerNumberInvalid");
      } else if (detailStr) {
        msg = detailStr;
      } else {
        msg = t("common.error");
      }
      console.error("[register] failed", { status, detail, message: err?.message, code: err?.code });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl border border-stone-border shadow-sm p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center mb-3">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-stone-text">{t("auth.registerTitle")}</h1>
          <p className="text-stone-muted text-sm">{t("auth.registerSubtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div className="grid grid-cols-2 gap-2">
            {["candidate", "employer"].map((r) => (
              <button
                key={r}
                type="button"
                data-testid={`register-role-${r}`}
                onClick={() => setForm((f) => ({ ...f, role: r }))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  form.role === r ? "bg-brand text-white border-brand" : "bg-white text-stone-sub border-stone-border"
                }`}
              >
                {t(`auth.${r}`)}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.name")}</label>
            <input data-testid="register-name" required value={form.name} onChange={set("name")} className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.email")}</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.password")}</label>
            <div className="relative">
              <input data-testid="register-password" type={showPw ? "text" : "password"} required value={form.password} onChange={set("password")} className="w-full px-4 py-3 pr-11 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
              <button type="button" data-testid="register-password-toggle" onClick={() => setShowPw((s) => !s)} tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-muted hover:text-brand">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.confirmPassword")}</label>
            <div className="relative">
              <input data-testid="register-confirm-password" type={showPw ? "text" : "password"} required value={form.confirm_password} onChange={set("confirm_password")} className="w-full px-4 py-3 pr-11 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
            </div>
            {form.confirm_password && form.password !== form.confirm_password && (
              <p className="text-xs text-red-500 mt-1" data-testid="register-password-mismatch">{t("auth.passwordMismatch")}</p>
            )}
          </div>
          {form.role === "employer" && (
            <div>
              <label className="block text-sm font-medium text-stone-sub mb-1">{t("auth.employerNumber")}</label>
              <input data-testid="register-employer-number" required value={form.employer_number} onChange={set("employer_number")} className="w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand" />
            </div>
          )}

          <button data-testid="register-submit" type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
            {loading ? t("auth.creating") : t("auth.registerBtn")}
          </button>
        </form>

        <p className="text-center text-sm text-stone-muted mt-5">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" data-testid="register-to-login" className="text-brand font-semibold hover:underline">
            {t("nav.login")}
          </Link>
        </p>
      </div>
    </div>
  );
}
